/**
 * Retention — how often customers come back, and how fast. Gross, from real
 * orders. Wired to /dashboard/retention-overview. (Refunds live on Operations
 * — 2026-06-12 IA decision: one question, one home.)
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate, SetupLocked, PullingData, SyncError } from "../lib/setup-gate.js";
import { TrendChart } from "../components/TrendChart.js";
import { FunnelChart } from "../components/FunnelChart.js";
import { Segmented } from "../components/Segmented.js";
import { Card, CardHead, Tile } from "../components/cards.js";
import { useDrill } from "../lib/drill.js";
import { usePeriod } from "../lib/period.js";
import { Hideable } from "../lib/hide.js";
import { SortableArea } from "../lib/sortable-cards.js";
import { useCmReady } from "../lib/use-cm-ready.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { PageLoadError } from "../components/PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface RetentionResponse {
  summary: { repeatRate: number | null; repeatRatePriorLens: number | null; repeatCohortNote: string; repeatHorizonDays: number | null; ordersPerCustomer: number; avgDaysToSecond: number | null; revenue: number; orders: number; customerCount: number };
  timeBetween: Array<{ step: string; avgDays: number; customers: number }>;
  newVsReturning: Array<{
    month: string; incomplete: boolean; newCustomers: number; returningCustomers: number; activeCustomers: number;
    newRev: number; returningRev: number; newCm: number; returningCm: number;
  }>;
  funnel: { rungs: Array<{ minOrders: number; customers: number }>; horizonDays: number | null; cohortNote: string };
}

export type RetentionValueMode = "customers" | "revenue" | "cm";

/** Never leave a previously selected CM view visible after profit readiness closes. */
export function resolveRetentionValueMode(mode: RetentionValueMode, cmReady: boolean): RetentionValueMode {
  return mode === "cm" && !cmReady ? "revenue" : mode;
}

const money = (n: number | null): string => (n === null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n));
const pct = (n: number | null): string => (n === null ? "—" : `${(n * 100).toFixed(1)}%`);

export default function RetentionPage(): React.ReactElement {
  const drill = useDrill();
  const api = useApiClient();
  const gate = useSetupGate();
  const period = usePeriod();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const cmReady = useCmReady();
  const capsQ = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => api.get<Capabilities>("/dashboard/capabilities"),
    enabled: ready,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const [nvrMode, setNvrMode] = useState<RetentionValueMode>("customers");
  const nvrDisplayMode = resolveRetentionValueMode(nvrMode, cmReady);
  // SKU-level by default on the retention-by-product card (matches the LTV page);
  // the card's toggle flips it to families.
  const [tbDrill, setTbDrill] = useState(false);
  const retentionQ = useQuery({
    queryKey: ["retention-overview", period.cacheKey],
    queryFn: () => api.get<RetentionResponse>("/dashboard/retention-overview", { ...period.query }),
    enabled: ready,
  });
  const data = retentionQ.data;
  const gateLoad = pageLoadState(gate);
  const requiredLoad = pageLoadState(capsQ, retentionQ);
  // Retention-by-first-product moved here from the LTV page. Reuses the
  if (gateLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your retention numbers"
        retrying={gateLoad.retrying}
        onRetry={gateLoad.retry}
      />
    );
  }
  if (gate.isLoading) return <p style={{ color: "var(--body)" }}>Loading…</p>;
  if (gate.data?.locked) return <SetupLocked />;
  if (gate.data?.syncStatus === "error") return <SyncError message={gate.data.errorMessage} />;
  if (gate.data?.syncStatus === "pending") return <PullingData />;
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your retention numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  if (capsQ.isLoading) return <p style={{ color: "var(--body)" }}>Checking customer history…</p>;
  if (capsQ.data?.grossCustomerValue !== true) {
    return (
      <div>
        <div style={kicker}>Retention</div>
        <h1 style={{ margin: "7px 0 16px", fontSize: 27 }}>Retention isn't available from this history</h1>
        <Card>
          <CardHead title="We need customer-level order history" />
          <p style={{ margin: "10px 0 0", color: "var(--body)", fontSize: 13.5, lineHeight: 1.55 }}>
            Sales can still appear in the dashboard, but this source does not identify customers and their orders reliably enough to measure repeat buying.
          </p>
        </Card>
      </div>
    );
  }

  // Computed headline: returning customers' share of recent revenue, from the
  // last 3 FULL months (the current partial month would understate it).
  const s = data?.summary;
  const horizon = s?.repeatHorizonDays ?? null; // repeat measured "within N days"
  const withinLabel = horizon ? `within ${horizon} days` : "";
  // ALL full months in the selected window (newVsReturning is already period-
  // scoped), not a fixed last-3 — so the headline matches the window (founder
  // 2026-07-07: 1yr selected but it said "over the last 3 full months").
  const fullMonths = (data?.newVsReturning ?? []).filter((m) => !m.incomplete);
  const retRev = fullMonths.reduce((t, m) => t + m.returningRev, 0);
  const allRev = fullMonths.reduce((t, m) => t + m.newRev + m.returningRev, 0);
  const retShare = allRev > 0 ? retRev / allRev : null;

  return (
    <div>
      <div style={kicker}>Retention</div>
      <h1 style={{ margin: "7px 0 8px", fontSize: 27 }}>Do customers come back?</h1>
      {s && retShare !== null ? (
        <p style={{ fontSize: 13.5, color: "var(--body)", maxWidth: 880, lineHeight: 1.55, margin: "10px 0 20px" }}>
          <b className="dd-num" style={{ color: "var(--num)" }}>{pct(s.repeatRate)}</b> of your customers come back
          for a second order{horizon ? ` ${withinLabel} of their first` : ""}{s.avgDaysToSecond !== null && <>. Among the people who returned in that same completed group, the average wait was <b className="dd-num" style={{ color: "var(--num)" }}>{Math.round(s.avgDaysToSecond)} days</b></>}.
          Over the {fullMonths.length} full month{fullMonths.length === 1 ? "" : "s"} in this window, returning customers drove <b className="dd-num" style={{ color: "var(--num)" }}>{money(retRev)}</b> —{" "}
          <b style={{ color: retShare >= 0.4 ? "var(--good)" : "var(--text)" }}>{(retShare * 100).toFixed(0)}%</b> of revenue.
        </p>
      ) : (
        <p style={{ color: "var(--body)", fontSize: 14.5, maxWidth: 800, lineHeight: 1.5, margin: "0 0 20px" }}>
          Keeping a customer is cheaper than finding a new one. Here's how often people reorder, and how fast.
        </p>
      )}

      {retentionQ.isLoading && <p style={{ marginTop: 20 }}>Loading…</p>}

      {data && (
        <SortableArea pageKey="retention">
          {<Hideable id="ret-repeat" label="Repeat rate" span={4}><Tile title="Repeat rate" value={pct(data.summary.repeatRate)} sub={horizon ? `come back within ${horizon}d` : "buy 2+ times"}
              explain={[
                `<b>What it is:</b> Of one fully matured customer group (${data.summary.repeatCohortNote}), the share who placed a second order within the selected runway.`,
                "<b>How it's calculated:</b> Standard date choices use the newest group that has fully completed the selected horizon. Custom dates keep the exact acquisition dates you chose, then include only customers who completed the full runway. This avoids mixing new customers with people who had much longer to reorder.",
                `<b>Cohort shown:</b> ${data.summary.repeatCohortNote}.`,
                data.summary.repeatRatePriorLens !== null
                  ? `<b>The older 'any-era' lens</b> — EVERY customer old enough (not just the most recent cohort) who reordered in the window — reads ${pct(data.summary.repeatRatePriorLens)}. The card shows the recent-cohort number because it's the cleaner decision basis.`
                  : "<b>The older 'any-era' lens</b> is not available for this group yet. The card shows the recent-cohort number because it is the cleaner decision basis.",
                "<b>Note:</b> Narrows with the product filter — it keeps only customers whose FIRST order included the product. Based on order counts, so costs don't change it. Same number as the Exec dashboard's Repeat rate.",
              ]} /></Hideable>}
          {<Hideable id="ret-opc" label="Orders per customer" span={4}><Tile title="Orders per customer" value={data.summary.ordersPerCustomer.toFixed(2)} sub={data.summary.repeatHorizonDays ? `first ${data.summary.repeatHorizonDays} days` : "lifetime avg"}
              explain={[
                "<b>What it is:</b> The average number of orders your newly acquired customers place within their first stretch, so you can watch whether recent customers are becoming more habitual over time.",
                "<b>How it's calculated:</b> Use the same fully matured customer group as Repeat rate and average how many orders each placed during the selected-length runway. Standard choices use the newest completed group. Custom dates keep the acquisition dates you chose. The All view uses lifetime orders per customer.",
                "<b>Example (sample data):</b> in the last 90 days, cohort customers averaged 1.56 orders in their first 90 days; over a full year that climbs to 2.69. Lifetime across everyone is 2.38, held down by recent buyers who have not had time to reorder.",
                "<b>Note:</b> Narrows with the product filter, keeping only customers whose FIRST order included the product. Follows the date picker now, so a shorter window reads lower because customers have had less time to reorder. Based on order counts.",
              ]} /></Hideable>}
          {/* "Days to 2nd order" tile removed (founder 2026-07-14): merged into the
              "Time between purchases" card below, whose first bar (1st to 2nd) IS
              this number. One card for the whole reorder rhythm, on both pages. */}

          {data.timeBetween.length > 0 && (
            <Hideable id="ret-time-between" label="Time between orders" span={12}>
            <Card onClick={() => setTbDrill(true)} className="dd-clickable-card" title="Click to see days-to-2nd-order by first product">
              <CardHead title="How long until they come back?" explain={[
                "<b>What it is:</b> The average wait between each order for customers who reached that next order. Smaller gaps at later steps mean the buying habit is getting stronger.",
                horizon
                  ? `<b>How it's calculated:</b> It uses the same completed customer group as Repeat rate: ${data.summary.repeatCohortNote}. Standard choices use the newest completed group, while custom dates keep the acquisition dates you chose. We count orders reached inside each customer's first ${horizon} days, then average the wait at each step.`
                  : "<b>How it's calculated:</b> It uses customers at least 30 days old and their full known order history, then averages the wait at each step among the people who reached it.",
                "<b>Example:</b> If 400 customers reached order two after an average of 52 days, the first bar shows 52 days and 400 customers. The count under each later bar will usually get smaller because fewer people reach that order.",
                "<b>Note:</b> Narrows with the product filter, keeping only customers whose FIRST order included the product. The count under each bar is the number who reached that step, not the size of the full customer group. The 1st-to-2nd bar is the old Days to 2nd order number, now folded into this one card.",
              ]} />
              <div style={{ marginTop: 12 }}>
                <TrendChart kind="stacked-bars" money={false} showValues
                  points={data.timeBetween.slice(0, 3).map((t, i) => ({
                    label: ["1st→2nd", "2nd→3rd", "3rd→4th"][i] ?? `→${t.step}`,
                    a: Math.round(t.avgDays),
                  }))} />
                <div style={{ display: "flex", gap: 16, marginTop: 6, fontSize: 10.5, color: "var(--faint)", justifyContent: "space-around" }}>
                  {data.timeBetween.slice(0, 3).map((t) => (
                    <span key={t.step} className="dd-num">{t.customers.toLocaleString()} customers</span>
                  ))}
                </div>
              </div>
              {data.timeBetween.length >= 2 && data.timeBetween[1]!.avgDays < data.timeBetween[0]!.avgDays && (
                <div style={{ fontSize: 12, color: "var(--body)", marginTop: 10 }}>
                  Gaps shrink as customers stick. That is a good sign the buying habit is getting stronger.
                </div>
              )}
            </Card>
            </Hideable>
          )}

          {data.funnel.rungs.length > 0 && (() => {
            // Flag 4: the funnel is the window's fully-completed cohort, order
            // depth counted within their first window-length — same composition
            // as Repeat rate, so the 1+→2+ cliff isn't steepened by just-joined
            // customers who haven't had time to reorder yet.
            const steps = data.funnel.rungs
              .map((r) => ({ label: `${r.minOrders}+ orders`, value: r.customers }))
              .filter((s) => s.value > 0);
            if (steps.length < 2) return null;
            const h = data.funnel.horizonDays;
            const depthLabel = h ? `within their first ${h} days` : "over their lifetime";
            return (
              <Hideable id="ret-funnel" label="Purchase frequency funnel" span={12}>
              <Card>
                <CardHead title="Purchase frequency funnel" explain={[
                  `<b>What it is:</b> For this window's cohort (${data.funnel.cohortNote}), how many reached each repeat depth — 1+, 2+, 3+, 4+, 5+ orders — ${depthLabel}, with the drop-off between each. The steepest drop is your biggest retention leak.`,
                  "<b>How it's calculated:</b> The same fully matured cohort as Repeat rate, with each rung counting how many reached that order depth within the runway. Standard choices use the newest completed group. Custom dates keep the acquisition dates you chose. Because everyone included has had the full runway, the first drop is not made worse by brand-new customers.",
                  "<b>Example:</b> of a fully-matured cohort, how many reached 2+, 3+, 4+, 5+ orders inside their window — a fair 1st→2nd read.",
                  "<b>Note:</b> Narrows with the product filter — it keeps only customers whose FIRST order included the product. Cohort + maturity based (flag 4): choose a 1-year view and it only includes people who've had a full year. 'All time' is the whole business over its lifetime. Based on order counts.",
                ]} />
                <div style={{ fontSize: 11.5, color: "var(--text-faint)", marginTop: 4 }}>{data.funnel.cohortNote}</div>
                <div style={{ marginTop: 12 }}>
                  <FunnelChart steps={steps} />
                </div>
              </Card>
              </Hideable>
            );
          })()}

          {(
          <Hideable id="ret-nvr" label="New vs returning" span={12}>
          <Card onClick={() => drill.open(nvrDisplayMode === "customers"
            ? { title: "Customers: new vs returning", metric: "customers", dimension: "customer_type" }
            : { title: `${nvrDisplayMode === "cm" ? "CM" : "Revenue"}: new vs returning`, metric: nvrDisplayMode === "cm" ? "cm" : "revenue", dimension: "customer_type" }
          )} className="dd-clickable-card" title="Click to explore">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
              <CardHead title={nvrDisplayMode === "customers" ? "Do customers come back? New vs returning" : `New vs returning ${nvrDisplayMode === "cm" ? "CM" : "revenue"}`} explain={[
                "<b>What it is:</b> Each month, your customers, revenue, or contribution margin (CM) split between first-time buyers and people coming back, with a returning-share line on top. A healthy business grows the returning band over time.",
                "<b>How it's calculated:</b> In the Customers view, each person counts once per month. New means their first paid order was that month. Returning means they bought before that month. In the Revenue and CM views, the first paid order is New and each later paid order is Returning. A fast second order in the same month still counts as Returning money. Free replacements do not count as orders, customers, or revenue. Their product and shipping costs still lower Returning CM in the month they were sent.",
                "<b>Example (sample data · year-to-date):</b> March 2026 had 1,018 new and 1,681 returning customers out of 2,699 active — a 62.3% returning share.",
                "<b>Note:</b> Narrows with the product filter, keeping only customers whose first order included the product. Months follow the selected dates, but new versus returning is decided from full order history so an existing customer is never mislabeled as new.",
              ]} />
              <Segmented
                ariaLabel="Customers, revenue, or contribution margin"
                options={[
                  { value: "customers", label: "Customers" },
                  { value: "revenue", label: "Revenue" },
                  ...(cmReady ? [{ value: "cm" as const, label: "CM" }] : []),
                ]}
                value={nvrDisplayMode}
                onChange={(v) => setNvrMode(v as RetentionValueMode)}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <TrendChart switchKinds={["stacked-bars", "grouped-bars", "table"]} chartKey="new-vs-returning"
                kind="stacked-bars"
                money={nvrDisplayMode !== "customers"}
                seriesNames={["Returning", "New"]}
                points={data.newVsReturning.map((m) => ({
                  label: m.month,
                  a: nvrDisplayMode === "customers" ? m.returningCustomers : nvrDisplayMode === "cm" ? m.returningCm : m.returningRev,
                  b: nvrDisplayMode === "customers" ? m.newCustomers : nvrDisplayMode === "cm" ? m.newCm : m.newRev,
                }))}
                lineOverlay={{
                  label: "Returning share",
                  values: data.newVsReturning.map((m) => {
                    // Customer groups are mutually exclusive by month, and their
                    // denominator is the exact distinct active population. Money
                    // remains a clean first-order vs later-order split.
                    const total = nvrDisplayMode === "customers" ? m.activeCustomers : nvrDisplayMode === "cm" ? m.returningCm + m.newCm : m.returningRev + m.newRev;
                    const ret = nvrDisplayMode === "customers" ? m.returningCustomers : nvrDisplayMode === "cm" ? m.returningCm : m.returningRev;
                    return total > 0 ? (ret / total) * 100 : null;
                  }),
                }}
              />
            </div>
            <div style={legend}>
              <span><i style={{ ...dot, background: "var(--accent)" }} />Returning</span>
              <span><i style={{ ...dot, background: "var(--accent-2)" }} />New</span>
              <span><i style={{ ...dot, background: "var(--c2)", borderRadius: 5, height: 3, marginTop: 4 }} />Returning share %</span>
            </div>
          </Card>
          </Hideable>
          )}


        </SortableArea>
      )}
      {tbDrill && data && (
        <TimeToSecondByProductModal
          onClose={() => setTbDrill(false)}
          horizonDays={data.summary.repeatHorizonDays}
          cohortNote={data.summary.repeatCohortNote}
        />
      )}
    </div>
  );
}

interface TbByProductRow { product: string; customers: number; avgDaysToSecond: number }

/** "How long until they come back?" drilled down by the customer's first product. */
export function TimeToSecondByProductModal({
  onClose,
  horizonDays,
  cohortNote,
}: {
  onClose: () => void;
  horizonDays: number | null;
  cohortNote: string;
}): React.ReactElement {
  const api = useApiClient();
  const period = usePeriod();
  const timeByProductQ = useQuery({
    queryKey: ["time-between-by-product", period.cacheKey],
    queryFn: () => api.get<{ rows: TbByProductRow[] }>("/dashboard/time-between-by-product", { ...period.query }),
  });
  const { data, isLoading } = timeByProductQ;
  const requiredLoad = pageLoadState(timeByProductQ);
  const rows = data?.rows ?? [];
  const maxDays = Math.max(1, ...rows.map((r) => r.avgDaysToSecond));
  const cell: React.CSSProperties = { padding: "8px 10px", fontSize: 13, borderBottom: "1px solid var(--border-faint)" };
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div role="dialog" style={{ width: 560, maxWidth: "100%", maxHeight: "88vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--shadow-pop)", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>How long until they come back? By first product</h2>
          <button onClick={onClose} aria-label="Close" style={{ border: "none", background: "transparent", color: "var(--body-faint)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
        <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--body)", lineHeight: 1.5 }}>
          This uses {cohortNote}. {horizonDays ? `It counts second orders reached inside each customer’s first ${horizonDays} days.` : "It uses their full known order history."} Products need at least 5 returning customers to appear. A longer wait may call for an earlier reminder or a subscription offer.
        </p>
        {requiredLoad.failed ? (
          <PageLoadError
            title="We couldn't load your retention numbers"
            retrying={requiredLoad.retrying}
            onRetry={requiredLoad.retry}
          />
        ) : isLoading ? (
          <div style={{ color: "var(--body)", fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: "var(--body)", fontSize: 13 }}>Not enough repeat data yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "var(--body-faint)", textAlign: "left" }}>
                <th style={{ ...cell, fontWeight: 600 }}>First product</th>
                <th style={{ ...cell, textAlign: "right", fontWeight: 600 }}>Customers</th>
                <th style={{ ...cell, fontWeight: 600, width: 200 }}>Days to 2nd order</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product}>
                  <td style={{ ...cell, color: "var(--text)", fontWeight: 600 }}>{r.product}</td>
                  <td style={{ ...cell, textAlign: "right", color: "var(--num)" }} className="dd-num">{r.customers.toLocaleString()}</td>
                  <td style={cell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 14, background: "var(--surface-2)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(r.avgDaysToSecond / maxDays) * 100}%`, background: "var(--accent)", opacity: 0.4, borderRadius: 4 }} />
                      </div>
                      <span className="dd-num" style={{ minWidth: 54, textAlign: "right", fontWeight: 700, color: "var(--num)" }}>{Math.round(r.avgDaysToSecond)} days</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const kicker: React.CSSProperties = { color: "var(--accent)", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" };
const legend: React.CSSProperties = { display: "flex", gap: 14, marginTop: 10, color: "var(--body)", fontSize: 12 };
const dot: React.CSSProperties = { display: "inline-block", width: 10, height: 10, borderRadius: 3, marginRight: 5, verticalAlign: -1 };
